/**
 * Coerenza della terna fiscale di riga/articolo (aliquota, natura, riferimento).
 *
 * Regola di dominio unica, valida identica su articolo, ordine fornitore e
 * DDT attivo:
 *   - aliquota IVA = numero (22, 10, 5, 4, 0);
 *   - Natura IVA e Riferimento Normativo esistono SOLO ad aliquota 0 e allora
 *     sono obbligatori (con le deroghe dichiarate per-superficie via policy);
 *   - ad aliquota ≠ 0 natura e riferimento sono vietati.
 *
 * Il catalogo trattamenti per tenant (`pos_vat_rates`) resta una fonte interna:
 * il servizio risolve da solo la riga trattamento a partire dalla terna. Questo
 * helper NON conosce il catalogo, valida solo la forma della terna.
 */
import { z } from 'zod';
import { isPosVatNatureCode } from './natura-iva-map';
import { isNaturaIntera, naturaInteraRejectionMessage } from './rt-admissibility';

/** Proiezione minima della terna: `vat_rate` assente = aliquota non dichiarata. */
export interface VatTripletInput {
  readonly vat_rate?: number | null;
  readonly vat_nature?: string | null;
  readonly riferimento_normativo?: string | null;
}

/**
 * Cosa è obbligatorio ad aliquota 0:
 *  - `nature_and_reference`: natura E riferimento (articolo);
 *  - `nature`: solo la natura (ordine fornitore: il riferimento è storia
 *    documentale, non input di ordine);
 *  - `none`: nulla di obbligatorio (DDT attivo: la riga eredita natura e
 *    riferimento dal prodotto quando non li porta).
 * In ogni policy ad aliquota ≠ 0 natura e riferimento sono vietati.
 */
export type VatTripletZeroRatePolicy = 'nature_and_reference' | 'nature' | 'none';

export interface VatTripletOptions {
  readonly zeroRate: VatTripletZeroRatePolicy;
  /**
   * Deroga per righe storiche già persistite (es. PO con `item_id`): ad
   * aliquota 0 l'obbligo scatta solo se la natura è stata dichiarata; una riga
   * legacy senza natura resta leggibile e scrivibile invariata.
   */
  readonly legacyZeroRateRow?: boolean;
}

export interface VatTripletIssue {
  readonly path: 'vat_rate' | 'vat_nature' | 'riferimento_normativo';
  readonly message: string;
}

export const VAT_TRIPLET_MESSAGES = {
  natureRequired: 'Natura IVA obbligatoria per una riga a IVA 0%.',
  referenceRequired: 'Riferimento Normativo obbligatorio quando è indicata la Natura IVA.',
  natureForbidden: 'Natura IVA e riferimento normativo sono ammessi solo per righe a IVA 0%.',
  natureUnknown: 'Codice Natura IVA non riconosciuto (atteso N1…N7, es. N2.1).',
} as const;

/** Codice d'errore BE per il rifiuto delle nature intere N2/N3/N6 su
 *  listino/prodotto/fattura (scarto SDI 00445). */
export const POS_FISCAL_FACE_NATURE_WHOLE_CODE = 'POS_FISCAL_FACE_NATURE_WHOLE';

function hasText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Restituisce le violazioni della terna (vuoto = coerente). Se `vat_rate` è
 * assente/null la terna non è valutabile per aliquota: si controlla solo che
 * un'eventuale natura sia un codice riconosciuto e porti il riferimento quando
 * la policy lo richiede.
 */
export function collectVatTripletIssues(input: VatTripletInput, options: VatTripletOptions): VatTripletIssue[] {
  const issues: VatTripletIssue[] = [];
  const nature = hasText(input.vat_nature) ? input.vat_nature.trim() : null;
  const reference = hasText(input.riferimento_normativo) ? input.riferimento_normativo.trim() : null;
  const rate = input.vat_rate ?? null;

  if (nature !== null && !isPosVatNatureCode(nature)) {
    issues.push({ path: 'vat_nature', message: VAT_TRIPLET_MESSAGES.natureUnknown });
  } else if (isNaturaIntera(nature)) {
    // Le nature intere restano legittime SOLO come aliquota di un reparto RT:
    // su articoli/listini/fatture serve il sotto-codice (scarto SDI 00445).
    issues.push({ path: 'vat_nature', message: naturaInteraRejectionMessage(nature) });
  }

  if (rate === null) {
    if (options.zeroRate === 'nature_and_reference' && nature !== null && reference === null) {
      issues.push({ path: 'riferimento_normativo', message: VAT_TRIPLET_MESSAGES.referenceRequired });
    }
    return issues;
  }

  if (rate !== 0) {
    if (nature !== null || reference !== null) {
      issues.push({ path: 'vat_nature', message: VAT_TRIPLET_MESSAGES.natureForbidden });
    }
    return issues;
  }

  if (options.zeroRate === 'none') return issues;
  const requiresMetadata = options.legacyZeroRateRow !== true || nature !== null;
  if (!requiresMetadata) return issues;
  if (nature === null) {
    issues.push({ path: 'vat_nature', message: VAT_TRIPLET_MESSAGES.natureRequired });
  }
  if (options.zeroRate === 'nature_and_reference' && reference === null) {
    issues.push({ path: 'riferimento_normativo', message: VAT_TRIPLET_MESSAGES.referenceRequired });
  }
  return issues;
}

/** True se la terna non ha violazioni per la policy indicata. */
export function isVatTripletCoherent(input: VatTripletInput, options: VatTripletOptions): boolean {
  return collectVatTripletIssues(input, options).length === 0;
}

/** Adapter `superRefine`: riporta ogni violazione sul path del campo. */
export function refineVatTriplet(
  input: VatTripletInput,
  ctx: z.RefinementCtx,
  options: VatTripletOptions,
): void {
  for (const issue of collectVatTripletIssues(input, options)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [issue.path], message: issue.message });
  }
}

/**
 * Terna normalizzata per la persistenza: ad aliquota ≠ 0 natura e riferimento
 * sono sempre null (nessun residuo), ad aliquota 0 sono i valori trimmati.
 * Non valida: chiamare prima `collectVatTripletIssues`.
 */
export function normalizeVatTriplet(input: VatTripletInput & { readonly vat_rate: number }): {
  vat_rate: number;
  vat_nature: string | null;
  riferimento_normativo: string | null;
} {
  if (input.vat_rate !== 0) return { vat_rate: input.vat_rate, vat_nature: null, riferimento_normativo: null };
  return {
    vat_rate: 0,
    vat_nature: hasText(input.vat_nature) ? input.vat_nature.trim() : null,
    riferimento_normativo: hasText(input.riferimento_normativo) ? input.riferimento_normativo.trim() : null,
  };
}
